import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ValidationUtilityService } from '../../../modules/shared/utils/validation.util/validation-utility.service';
import { AuthService } from '../../../services/core/auth.service';
import { ActivatedRoute, Router, NavigationExtras } from '@angular/router';
import { SpinnerComponent } from '../../../modules/shared/spinner/spinner.component';
import { ServiceRouter } from '../../../services/core/http-service-router.service';
import { METHOD_TYPE } from '../../../constants/service.constants';
import { VerificationBean } from '../../../beans/account/account-module.bean';
import { Config } from '../../../config/config';
import { Environment } from '../../../config/environment';
import { Subscription } from 'rxjs';
import { AppUtil } from '../../../utils/app.util';
import { ProfileRelatedInfoBean } from '../../../beans/account/account-module.bean';
import { ProfileService } from '../../../services/core/profile.service';
import { FeedsService } from '../../../services/dashboard/feeds.service';
import { DataCarrierService } from '../../../services/core/data-carrier.service';
import { AppointmentsService } from '../../../services/scheduling/appointment/appointments.service';
import { AUTH_COMPONENTS, INCENTIVE_CONSTANTS } from 'src/app/constants/scheduling.constants';
import { passwordSpecialCharsValidatorRegExp } from 'src/app/constants/app.constants';
import { MfaService } from 'src/app/services/core/mfa.service';

/**
 * Email Verification component
 */
@Component({
	selector: 'app-email-verification',
	templateUrl: './email-verification.component.html',
	styleUrls: ['./email-verification.component.scss'],
})
export class EmailVerificationComponent implements OnInit, OnDestroy {
	/** Create password form */
	createPasswordForm: FormGroup;
	/** holds response from cognito */
	cognitoResponse: any;
	/** holds data for cognito */
	userBean: any = { email: '', password: '', epmsPID: '', uuid: '' };
	/** subscriptions used */
	subscriptions: Subscription[] = [];
	/**  */
	canLoad: boolean = false;
	/** denote whether cognito account creation failed or not */
	accountCreationFailed: boolean = false;
	/** Return Url */
	returnUrl: string = '';
	/** Flag to hide password strength block on focus */
	hidePasswordStrengthBlock: boolean = true;
	/** Flag which says verification link has been resent or not */
	linkResent: boolean = false;
	/** Custom Password Special Characters Validator RegExp */
	passwordSpecialCharsValidatorRegExp: RegExp = passwordSpecialCharsValidatorRegExp;

	/**
	 * Email Verification component constructor
	 * @param router - Angular Router
	 * @param authService - Auth Service
	 * @param fb - Angular Form Builder
	 * @param spinner - Spinner Component
	 * @param activatedRoute - Angular Activated route
	 * @param config - URL Config
	 * @param environment - Environment Config
	 * @param serviceRouter - Service router
	 */
	constructor(
		private router: Router,
		private authService: AuthService,
		private fb: FormBuilder,
		private spinner: SpinnerComponent,
		private activatedRoute: ActivatedRoute,
		private config: Config,
		private environment: Environment,
		private serviceRouter: ServiceRouter,
		private profileService: ProfileService,
		private feedsService: FeedsService,
		private appointmentService: AppointmentsService,
		private dataCarrierService: DataCarrierService,
		private mfaService: MfaService,
	) {
		this.subscriptions.push(
			this.activatedRoute.queryParams.subscribe(params => {
				try {
					this.canLoad = false;
					if (params && params.activate) {
						const queryParamsObj = JSON.parse(atob(params.activate));
						this.userBean.email = AppUtil.getFormattedEmail(queryParamsObj.email);
						this.userBean.epmsPID = queryParamsObj.epmsPID.toString();
						this.userBean.uuid = queryParamsObj.uuid;
						this.userBean.additionalData = queryParamsObj.additionalData;
						const request = new VerificationBean();
						request.uuid = this.userBean.uuid;
						request.emailAddress = this.userBean.email;
						request.verificationCode = queryParamsObj.code;
						this.verifyCode(request);
					} else if (params && params.params) {
						const queryParamsObj = JSON.parse(atob(params.params));
						this.userBean.email = AppUtil.getFormattedEmail(queryParamsObj.email);
						this.userBean.epmsPID = queryParamsObj.epmsPID.toString();
						this.userBean.uuid = queryParamsObj.uuid;
						this.canLoad = true;
					} else {
						this.router.navigateByUrl('/');
					}
					if (params.returnUrl) {
						this.returnUrl = params.returnUrl ? decodeURI(params.returnUrl) : '';
					}
				} catch (Exception) {
					this.router.navigateByUrl('/');
				}
			})
		);
	}

	/** resend Verification code */
	resendVerificationCode() {
		const request = new VerificationBean();
		request.uuid = this.userBean.uuid;
		request.emailAddress = this.userBean.email;
		request.additionalData = this.userBean.additionalData;
		request.registration = true;
		this.spinner.startLoader();
		this.subscriptions.push(
			this.serviceRouter
				.makeRequest(
					this.environment.getEnvironment().EPMS_PROXY_BASE_URL,
					this.config.url.registrationApi.createAccount + `/${this.userBean.epmsPID}/verification/resend`,
					METHOD_TYPE.GENERAL_POST,
					request
				)
				.subscribe(
					(response: any) => {
						this.linkResent = true;
						this.spinner.stopLoader();
					},
					err => {
						this.spinner.stopLoader();
						console.error(
							`[${EmailVerificationComponent.name}][${this.resendVerificationCode.name}]`,
							'Error occurred while resending verification code during registration flow to email : ',
							this.userBean.email,
							err
						);
					}
				)
		);
	}

	verifyCode(verificationCodeRequest) {
		this.subscriptions.push(
			this.serviceRouter
				.makeRequest(
					this.environment.getEnvironment().EPMS_PROXY_BASE_URL,
					this.config.url.registrationApi.createAccount + `/${this.userBean.epmsPID}/verification`,
					METHOD_TYPE.GENERAL_POST,
					verificationCodeRequest
				)
				.subscribe(
					(response: any) => {
						this.spinner.stopLoader();
						this.canLoad = true;
					},
					err => {
						this.spinner.stopLoader();
						this.canLoad = false;
						//display some err message that the link became  invalid
						console.error(
							`[${EmailVerificationComponent.name}][${this.verifyCode.name}]`,
							'Error occurred while sending verification code during registration flow to email : ',
							this.userBean.email,
							err
						);
					}
				)
		);
	}

	/** Constructing the password form on component init */
	ngOnInit() {
		this.createPasswordForm = this.fb.group(
			{
				password: new FormControl(
					'',
					Validators.compose([Validators.required, ValidationUtilityService.passwordValidator])
				),
				confirmPassword: new FormControl(
					'',
					Validators.compose([Validators.required, ValidationUtilityService.passwordValidator])
				),
			},
			{ validator: ValidationUtilityService.matchingPasswords.bind(this) }
		);
	}

	/**
	 *  Registering the user to cognito with given  Email ID, Password and EPMS ID
	 */
	createCognitoAccount() {
		if (this.createPasswordForm.valid) {
			const authCredentials = this.createPasswordForm.getRawValue();
			this.cognitoResponse = {};
			this.userBean.password = authCredentials.password;
			this.spinner.startLoader();
			this.authService.signUpUser(this.userBean);
			this.subscriptions.push(
				this.authService.getSignUpResult().subscribe(
					res => {
						this.spinner.stopLoader();
						this.cognitoResponse = res;
						this.updateAccountStatus();
					},
					error => {
						console.error(
							`[${EmailVerificationComponent.name}][${this.createCognitoAccount.name}]`,
							'Error while creating user account in cognito',
							this.userBean.email,
							error
						);
						this.accountCreationFailed = true;
						this.spinner.stopLoader();
					}
				)
			);
		} else {
			this.createPasswordForm.get('password').markAsTouched();
			this.createPasswordForm.get('confirmPassword').markAsTouched();
		}
	}

	/**
	 * Login the user immediately after creating account
	 */
	login() {
		this.spinner.startLoader();
		console.info('Activated account');
		this.authService.login(this.userBean.email, this.createPasswordForm.getRawValue().password, AUTH_COMPONENTS.SIGNUP_COMPONENT);
		this.subscriptions.push(
			this.authService.getResult().subscribe(response => {
				if (this.router.routerState.snapshot.url.includes('embed')) {
					this.spinner.stopLoader();
					window.open(`${window.location.origin}/dashboard`, '_top');
				} else {
					// check if we have returnUrl directly or through registration flow
					if (this.userBean.additionalData['returnUrl']) {
						this.returnUrl = this.userBean.additionalData['returnUrl'] || '';
					}
					if (this.returnUrl !== '') {
						this.profileService.getProfileInfo().subscribe(
							(res: ProfileRelatedInfoBean) => {
								this.spinner.stopLoader();
								const profileInfo = res;
								if (profileInfo.profileInfo.userMatched) {
									if (this.returnUrl.includes('scheduling/create/appointment')) {
										sessionStorage.setItem('RETURN_URL', 'true');
									}
									this.router.navigateByUrl(`${this.returnUrl}`);
								} else {
									this.router.navigate(['dashboard']);
								}
							},
							err => {
								this.spinner.stopLoader();
							}
						);
					} else {
						AppUtil.redirectToScheduling(
							this.router,
							this.feedsService,
							this.appointmentService,
							this.dataCarrierService,
							//optionally send the promo/referral data if present
							this.userBean.additionalData[INCENTIVE_CONSTANTS.RETURN_URL_ADDITIONAL_DATA_KEY] || {}
						);
					}
				}
			})
		);
	}

	/** redirect to login page */
	redirectToLogin() {
		let navigationPath = 'login';
		if (this.router.routerState.snapshot.url.includes('embed')) {
			navigationPath = 'embed/login';
		} else {
			const navigationParams: NavigationExtras = {
				queryParams: { clearHistory: true },
			};
			if (this.returnUrl !== '') {
				navigationParams.queryParams['returnUrl'] = this.returnUrl;
			}
			this.router.navigate([navigationPath], navigationParams);
		}
	}

	/** Update account status on backend */
	updateAccountStatus() {
		this.subscriptions.push(
			this.serviceRouter
				.makeRequest(
					this.environment.getEnvironment().EPMS_PROXY_BASE_URL,
					this.config.url.registrationApi.createAccount + `/${this.userBean.epmsPID}`,
					METHOD_TYPE.GENERAL_PUT,
					{
						uuid: this.userBean.uuid,
						email: this.userBean.email,
					}
				)
				.subscribe(
					response => {
						this.login();
						sessionStorage.setItem('createAccountToaster', '1');
					},
					error => {
						console.error(
							`[${EmailVerificationComponent.name}][${this.updateAccountStatus.name}]`,
							error,
							this.userBean.email,
							this.userBean.uuid
						);
					}
				)
		);
	}

	/** handling the type of validation error on confirm password field */
	checkIfConfirmPasswordHasErrors() {
		if (this.createPasswordForm.get('password').hasError('invalidPassword')) {
			if (
				this.createPasswordForm.get('confirmPassword').errors &&
				!this.createPasswordForm.get('confirmPassword').hasError('required')
			) {
				this.createPasswordForm.get('confirmPassword').setErrors({ invalidPassword: true });
			}
		} else {
			if (
				this.createPasswordForm.get('confirmPassword').errors &&
				!this.createPasswordForm.get('confirmPassword').hasError('required')
			) {
				this.createPasswordForm.get('confirmPassword').setErrors({ mismatchedPasswords: true });
			}
		}
		if (
			this.createPasswordForm.get('password').hasError('invalidPassword') &&
			this.createPasswordForm.get('confirmPassword').hasError('invalidPassword') &&
			this.createPasswordForm.get('confirmPassword').touched
		) {
			return true;
		} else {
			return false;
		}
	}

	getGlobalMfaStatus() {
		return this.mfaService.ss_getGlobalMfaStatus();
	}

	/** Unsubscribing all the subscriptions used */
	ngOnDestroy() {
		// prevent memory leak when component destroyed
		this.subscriptions.forEach(subscription => subscription.unsubscribe());
		this.subscriptions = [];
	}
}
